import React, { ReactNode, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  CssBaseline,
  Box,
  Avatar,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess,
  ExpandMore,
  Home as HomeIcon,
  Savings as SavingsIcon,
  People as PeopleIcon,
  Add as AddIcon,
  ListAlt as ListAltIcon,
  SupervisorAccount as ConsultantIcon,
  Assignment as TasksIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Inbox as InboxIcon,
} from "@mui/icons-material";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import { Link, useLocation } from "react-router-dom";

import { AuthUserDetails } from "./AuthUserDetails";
import NotificationBell from "./NotificationBell";
import GlobalSnackbar from "./GlobalSnackbar";
// import AddToHomeScreen from "./AddToHomeScreen";
import { LinkWithParams } from "./LinkWithParams";
import { useHasActiveGoogleEmail } from "../hooks/integration/useIntegrations";

const drawerWidth = 240;
const collapsedWidth = 60;

// הגדרת טיפוס התפריט
interface MenuItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  subMenu?: MenuItem[]; // תפריט משנה אופציונלי
}

interface NavbarProps {
  toggleTheme: () => void;
}

// פונקציה שמחזירה את התפריט בהתאם לאינטגרציות
const getMenuConfig = (hasGoogleEmail: boolean): MenuItem[] => {
  const baseMenu: MenuItem[] = [
    {
      label: "גמל-נט",
      icon: <HomeIcon />,
      path: "/home",
    },
    {
      label: "ניהול",
      icon: <FolderSharedIcon />,
      path: "/users",
    },
    {
      label: "ייעוצים",
      icon: <ConsultantIcon />,
      path: "/consulting",
    },
  ];

  // הוספת תיבת דואר רק אם יש אינטגרציית Google Email פעילה
  if (hasGoogleEmail) {
    baseMenu.push({
      label: "תיבת דואר",
      icon: <InboxIcon />,
      path: "/inbox",
    });
  }

  return baseMenu;
};

export const Navbar: React.FC<NavbarProps & { children?: ReactNode }> = ({
  children,
  toggleTheme,
}) => {
  const [open, setOpen] = React.useState(false);
  const [openSubMenu, setOpenSubMenu] = React.useState<string | null>(null);
  const isMobile = useMediaQuery("(max-width:600px)");
  const location = useLocation();

  // בדיקה אם יש אינטגרציית Google Email פעילה
  const { hasActiveGoogleEmail } = useHasActiveGoogleEmail();

  // יצירת התפריט בהתאם לאינטגרציות
  const menuConfig = React.useMemo(
    () => getMenuConfig(hasActiveGoogleEmail),
    [hasActiveGoogleEmail]
  );

  useEffect(() => {
    setOpen(false); // סגור את הפאנל בכל טעינת דף
  }, [location.pathname]);

  
  const handleMenuClick = (path: string) => {
    if (location.pathname !== path) {
      setOpen(false); // סגור את הפאנל אם עברנו לדף חדש
    }
  };

  const handleExitFullscreen = () => {
    //    setLockFullscreen(false);

    console.log("handleExitFullscreen");
  };

  const toggleSubMenu = (label: string) => {
    setOpenSubMenu((prev) => (prev === label ? null : label));
  };

  const isSelected = (path: string) => location.pathname === path;

  // הסתרת Navbar בדפי login ו-print
  const isPrintPage = location.pathname.includes("/print");
  if (location.pathname === "/login" || isPrintPage) {
    return null;
  }

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {/* Navbar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setOpen(!open)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Rev & U
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            {/* הוספת כפתור התקנה רק אם צריך */}
            {/* <AddToHomeScreen /> */}
            <AuthUserDetails /> {/* הוספת הקומפוננטה בצד ימין ביותר */}
             <NotificationBell />
            <IconButton color="inherit" onClick={toggleTheme}>
              <Brightness4Icon />
            </IconButton>
          
          </Box>
          <GlobalSnackbar />
          {/* הצגת תוכן כאשר מצב FULLSCREEN פעיל */}
          {/* הצגת תוכן כאשר מצב FULLSCREEN פעיל */}
          {/* {selectedToView && (
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              sx={{
                padding: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <Typography variant="h6">
                {selectedToView.name} ({selectedToView.viewedType})
              </Typography>
              <Avatar>{selectedToView.name.charAt(0).toUpperCase()}</Avatar>
              <Box
                component="button"
                onClick={() => handleExitFullscreen()}
                sx={{
                  marginLeft: "auto",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "1.5rem",
                  color: "#ff1744",
                  "&:hover": { color: "#d50000" },
                }}
              >
                X
              </Box>
            </Box>
          )} */}
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        anchor="left"
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        sx={{
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedWidth,
            transition: "width 0.3s",
            overflowX: "hidden",
            boxSizing: "border-box",
            backgroundColor: "#000",
            color: "#fff",
          },
        }}
      >
        <Toolbar />
        <Box display="flex" justifyContent="flex-end" px={1}>
          <IconButton onClick={() => setOpen(!open)} sx={{ color: "#fff" }}>
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
        <List>
          {menuConfig.map((item, index) => (
            <React.Fragment key={index}>
              <ListItemButton
                onClick={
                  item.subMenu
                    ? () => toggleSubMenu(item.label)
                    : () => handleMenuClick(item.path)
                }
                component={!item.subMenu ? LinkWithParams : undefined}
                to={!item.subMenu ? item.path : undefined}
                selected={isSelected(item.path)}
                sx={{
                  bgcolor: isSelected(item.path)
                    ? "rgba(255, 152, 0, 0.2)"
                    : "transparent",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                  pl: open ? 2 : 1,
                }}
              >
                <Tooltip title={open ? "" : item.label} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: "auto", // דריסה של min-width
                      color: isSelected(item.path) ? "#ff9800" : "#fff",
                      backgroundColor: isSelected(item.path)
                        ? "rgba(128, 128, 128, 0.3)" // צבע עם SELECTED
                        : "transparent", // צבע ללא SELECTED
                      borderRadius: "50%", // תמיד עיגול
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      "&:hover": {
                        backgroundColor: "rgba(128, 128, 128, 0.1)", // צבע בהיר יותר כשהעכבר מעל
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                </Tooltip>
                {open && <ListItemText primary={item.label} />}
                {item.subMenu &&
                  (openSubMenu === item.label ? (
                    <ExpandLess sx={{ color: "#fff" }} />
                  ) : (
                    <ExpandMore sx={{ color: "#fff" }} />
                  ))}
              </ListItemButton>
              {item.subMenu && (
                <Collapse
                  in={openSubMenu === item.label && open}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.subMenu.map((subItem, subIndex) => (
                      <ListItemButton
                        key={subIndex}
                        component={LinkWithParams}
                        to={subItem.path}
                        selected={isSelected(subItem.path)}
                        onClick={() => handleMenuClick(subItem.path)}
                        sx={{
                          pl: 4,
                          bgcolor: isSelected(subItem.path)
                            ? "rgba(255, 152, 0, 0.2)"
                            : "transparent",
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: isSelected(subItem.path)
                              ? "#ff9800"
                              : "#fff",
                            ...(isSelected(subItem.path) && {
                              backgroundColor: "rgba(128, 128, 128, 0.3)",
                              borderRadius: "50%",
                              width: "40px",
                              height: "40px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }),
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText primary={subItem.label} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          marginLeft: open ? `${drawerWidth}px` : `${collapsedWidth}px`,
          marginTop: "8px",
          overflow: "auto",
          transition: "margin 0.3s",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export {};
